import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Segmented, Skeleton, Table, Tag, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  StockRegister,
  StockRegisterDay,
  StockRegisterRow,
  normaliseStockRegister,
  siteDailyApis,
} from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { fmtQty } from "../executionNorm";
import { matchesSearch } from "./stockSheet";
import { UNCATEGORIZED } from "./materialCategories";

// The store's ledger read DAY BY DAY — the register the site files, as against
// the single-day sheet the store enters on.
//
// Only days that MOVED appear. A day with no row is not a gap: the balance
// carried, which is the whole point of the sparse model, and a row per material
// per calendar day would be 2,000 rows of nothing in a month. The header says so
// rather than leaving the reader to wonder where Tuesday went.
//
// Every figure is read back from what was STORED — never recomputed here — so the
// register agrees with the sheet that wrote it, including after a back-dated
// correction restated the days in between.

const MUTED = "#8094ae";
const INK = "#364a63";
const READ_ONLY = "#526484";
const WARN = "#d9a545";
const BAD = "#cf1322";

/** Does this row have anything the site has to explain? */
const isFlagged = (row: StockRegisterRow) =>
  row.purchaseAboveNorm ||
  row.targetState === "OVER" ||
  row.varianceState === "STORE_HIGHER" ||
  row.varianceState === "STORE_LOWER" ||
  Number(row.closingQty) < 0;

// A figure that failed a check is shown in red WITH the amount it failed by
// underneath — the check lives on the number it is about, not in a column of its
// own at the end of the row.
const money = (value: string, opts: { strong?: boolean; danger?: boolean } = {}) => {
  const n = Number(value);
  if (value === "" || !Number.isFinite(n)) return <span style={{ color: MUTED }}>—</span>;
  return (
    <span
      className="exec-num"
      style={{ color: opts.danger ? BAD : INK, fontWeight: opts.danger || opts.strong ? 600 : 400 }}
    >
      ₹ {fmtQty(n)}
    </span>
  );
};
const qty = (value: string, opts: { danger?: boolean } = {}) => {
  const n = Number(value);
  if (value === "" || !Number.isFinite(n)) return <span style={{ color: MUTED }}>—</span>;
  const danger = opts.danger || n < 0;
  return (
    <span className="exec-num" style={{ color: danger ? BAD : INK, fontWeight: danger ? 600 : 400 }}>
      {fmtQty(n)}
    </span>
  );
};

interface FlagCounts {
  overNorm: number;
  overTarget: number;
  mismatch: number;
  negative: number;
}

interface CategoryBlock {
  key: string; // `CAT-${categoryId}` — the convention every category surface uses
  categoryId: number;
  categoryName: string;
  rows: StockRegisterRow[];
  purchasedValue: number;
  issuedValue: number;
  closingValue: number;
  flags: FlagCounts;
}

const anyFlag = (f: FlagCounts) => f.overNorm + f.overTarget + f.mismatch + f.negative > 0;

/**
 * The checks, as chips on a summary line. They are what a collapsed block has to
 * say for itself: a category that needs attention must be visible without being
 * opened, or the accordion hides the very thing the register is read for.
 */
const FlagChips: React.FC<{ flags: FlagCounts }> = ({ flags }) => {
  if (!anyFlag(flags)) return null;
  const chip = (n: number, label: string, tone: string, title: string) =>
    n > 0 ? (
      <Tooltip title={title} key={label}>
        <span
          style={{
            color: tone,
            border: `1px solid ${tone}`,
            borderRadius: 10,
            padding: "0 7px",
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {n} {label}
        </span>
      </Tooltip>
    ) : null;
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      {chip(flags.overNorm, "over norm", BAD, "Bought above the price the norms assume — every target costed on that material is understated.")}
      {chip(flags.overTarget, "over allocation", BAD, "Issued more than that day's norms allocated to the tasks that produced.")}
      {chip(flags.mismatch, "vs tasks", WARN, "The store's issue differs from what the tasks booked in Material-Stores.")}
      {chip(flags.negative, "negative", BAD, "Closing below zero — a receipt is probably not recorded yet.")}
    </span>
  );
};

/**
 * A day's movements split by material category, name-ascending with
 * uncategorised last — the same order the entry sheet blocks in, so the two
 * views of one day read down in the same sequence.
 */
const byCategory = (rows: StockRegisterRow[]): CategoryBlock[] => {
  const blocks = new Map<number, CategoryBlock>();
  rows.forEach((row) => {
    const categoryId = Number(row.categoryId) || 0;
    let block = blocks.get(categoryId);
    if (!block) {
      block = {
        key: `CAT-${categoryId}`,
        categoryId,
        categoryName: row.categoryName || UNCATEGORIZED,
        rows: [],
        purchasedValue: 0,
        issuedValue: 0,
        closingValue: 0,
        flags: { overNorm: 0, overTarget: 0, mismatch: 0, negative: 0 },
      };
      blocks.set(categoryId, block);
    }
    block.rows.push(row);
    block.purchasedValue += Number(row.purchasedValue) || 0;
    block.issuedValue += Number(row.issuedValue) || 0;
    block.closingValue += Number(row.closingValue) || 0;
    if (row.purchaseAboveNorm) block.flags.overNorm += 1;
    if (row.targetState === "OVER") block.flags.overTarget += 1;
    if (row.varianceState === "STORE_HIGHER" || row.varianceState === "STORE_LOWER") block.flags.mismatch += 1;
    if (Number(row.closingQty) < 0) block.flags.negative += 1;
  });
  return Array.from(blocks.values()).sort((a, b) => {
    if (a.categoryId === 0) return 1;
    if (b.categoryId === 0) return -1;
    return a.categoryName.localeCompare(b.categoryName);
  });
};

/** The line under a figure that says what it exceeded, and by how much. */
const note = (text: React.ReactNode, tone: string = BAD) => (
  <div className="pms-exec-hint" style={{ color: tone, fontWeight: 600 }}>
    {text}
  </div>
);

const StockRegisterSection: React.FC<{
  projectId: number;
  projectName: string;
  fromDate: string;
  toDate: string;
  search: string;
  reloadToken: number;
  onLoaded?: (register: StockRegister | null) => void;
}> = ({ projectId, projectName, fromDate, toDate, search, reloadToken, onLoaded }) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  const [register, setRegister] = useState<StockRegister | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // A register is read to find what needs explaining, so it can be narrowed to
  // exactly that. Local state: it is a way of reading, not a filter on the data.
  const [show, setShow] = useState<"all" | "flagged">("all");
  // Accordion overrides. The DEFAULT is computed, not stored: a category with a
  // flag opens itself, everything else stays shut. Only what the reader clicks
  // lands here, and it is dropped whenever the data or the view changes, so a
  // stale "closed" cannot hide a flag on the next day.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);
  // Org branding for the export, read the same way every report reads it: the
  // currentUser slice holds the raw /users/currentUser payload.
  const currentUser = useSelector((state: any) => state.currentUser?.data);

  const load = useCallback(() => {
    if (!projectId || !fromDate || !toDate) return;
    setLoading(true);
    siteDailyApis
      .stockRegister(projectId, { fromDate, toDate })
      .then((res) => {
        if (res?.status !== true) {
          setRegister(null);
          setError(res?.message || "Could not load the stock register");
          return;
        }
        setError("");
        setRegister(normaliseStockRegister(res.data));
      })
      .catch(() => {
        setRegister(null);
        setError("Could not load the stock register");
      })
      .finally(() => setLoading(false));
  }, [projectId, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => {
    onLoaded?.(register);
  }, [register, onLoaded]);

  useEffect(() => {
    setToggled({});
  }, [register, show, search]);

  // The search narrows the MATERIAL rows; a day with nothing left drops out, so
  // "cement" answers "which days did cement move, and by how much".
  const days = useMemo<StockRegisterDay[]>(() => {
    if (!register) return [];
    const term = search.trim();
    if (!term && show === "all") return register.days;
    return register.days
      .map((day) => ({
        ...day,
        rows: day.rows.filter(
          (row) => (!term || matchesSearch(row as any, search)) && (show === "all" || isFlagged(row))
        ),
      }))
      .filter((day) => day.rows.length);
  }, [register, search, show]);

  // The export takes the register as LOADED, not as filtered: a register is a
  // record of the day, and a file that silently omits whatever was typed in the
  // search box would be the wrong document to file. The search is written into
  // the title block instead, so the reader knows what they are holding.
  const handleExport = async () => {
    if (!register) return;
    setExporting(true);
    try {
      const helpers = await import("../../reports/shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(currentUser?.orgLogo);
      const { default: exportWorkbook } = await import("./stockRegisterWorkbook");
      await exportWorkbook({
        register,
        projectName,
        orgName: currentUser?.orgName,
        logo,
        searchText: search,
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting the stock register:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Grouped once per day and cached: the header, the count and the body all read
  // it, and regrouping in each would be three passes over the same rows.
  const blocksByDay = useMemo(() => {
    const map = new Map<string, CategoryBlock[]>();
    days.forEach((day) => map.set(day.date, byCategory(day.rows)));
    return map;
  }, [days]);
  const blocksOf = (day: StockRegisterDay) => blocksByDay.get(day.date) ?? [];

  const filtered = search.trim() !== "" || show === "flagged";
  const flaggedCount =
    (register?.overNormCount ?? 0) +
    (register?.overTargetCount ?? 0) +
    (register?.mismatchCount ?? 0) +
    (register?.negativeCount ?? 0);
  // One date is the usual case — the page carries a single day everywhere — but
  // the query takes a range, so the copy handles both rather than lying about it.
  const oneDay = fromDate === toDate;
  const shownRows = useMemo(() => days.reduce((acc, day) => acc + day.rows.length, 0), [days]);

  const rowColumns = [
    {
      title: "Material",
      key: "material",
      width: w(260),
      render: (_: any, row: StockRegisterRow) => (
        <div>
          <div style={{ color: "#022c57", fontWeight: 600 }}>{row.materialName}</div>
          <div className="pms-exec-hint">
            {row.uomName || "no unit"}
            {row.productCode ? ` · ${row.productCode}` : ""}
          </div>
        </div>
      ),
    },
    { title: "Opening", key: "oq", width: w(100), align: "right" as const, render: (_: any, r: StockRegisterRow) => qty(r.openingQty) },
    { title: "Purchased", key: "pq", width: w(100), align: "right" as const, render: (_: any, r: StockRegisterRow) => qty(r.purchasedQty) },
    {
      title: "Pur. rate",
      key: "pr",
      width: w(125),
      align: "right" as const,
      render: (_: any, r: StockRegisterRow) => {
        const over = Number(r.purchasedRate) - Number(r.normRate);
        return (
          <Tooltip
            title={
              r.purchaseAboveNorm
                ? `The norms price this material at ₹${fmtQty(Number(r.normRate) || 0)}. Every target costed on it is understated by the difference.`
                : undefined
            }
          >
            <div>
              {money(r.purchasedRate, { danger: r.purchaseAboveNorm })}
              {r.purchaseAboveNorm && note(`+₹${fmtQty(over)} over norm`)}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Issued",
      key: "iq",
      width: w(140),
      align: "right" as const,
      render: (_: any, r: StockRegisterRow) => {
        const over = r.targetState === "OVER";
        const excess = Number(r.issuedQty) - Number(r.targetQty);
        const mismatch = r.varianceState === "STORE_HIGHER" || r.varianceState === "STORE_LOWER";
        return (
          <div>
            {qty(r.issuedQty, { danger: over })}
            {over && (
              <Tooltip title={`That day's norms allocated ${fmtQty(Number(r.targetQty) || 0)} across ${r.targetTaskCount} producing task(s).`}>
                {note(`+${fmtQty(excess)} over allocation`)}
              </Tooltip>
            )}
            {mismatch && (
              <Tooltip title={`The store issued ${fmtQty(Number(r.issuedQty) || 0)}; the tasks booked ${fmtQty(Number(r.taskIssuedQty) || 0)}.`}>
                {note(
                  <>
                    {Number(r.varianceQty) > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
                    {Number(r.varianceQty) > 0 ? "+" : ""}
                    {fmtQty(Number(r.varianceQty) || 0)} vs tasks
                  </>,
                  r.varianceState === "STORE_HIGHER" ? WARN : BAD
                )}
              </Tooltip>
            )}
          </div>
        );
      },
    },
    { title: "Issued ₹", key: "iv", width: w(110), align: "right" as const, render: (_: any, r: StockRegisterRow) => money(r.issuedValue) },
    {
      title: "Allocated",
      key: "tq",
      width: w(105),
      align: "right" as const,
      render: (_: any, r: StockRegisterRow) =>
        r.targetQty === "" ? (
          <Tooltip title="No task carrying this material produced anything that day, so the norms allocated none of it.">
            <span style={{ color: MUTED }}>—</span>
          </Tooltip>
        ) : (
          <Tooltip title={`What that day's norms allocated across ${r.targetTaskCount} producing task(s).`}>
            <span className="exec-num" style={{ color: READ_ONLY }}>{fmtQty(Number(r.targetQty))}</span>
          </Tooltip>
        ),
    },
    { title: "Norm rate", key: "nr", width: w(100), align: "right" as const, render: (_: any, r: StockRegisterRow) => money(r.normRate) },
    { title: "Avg rate", key: "ar", width: w(100), align: "right" as const, render: (_: any, r: StockRegisterRow) => money(r.avgRate) },
    {
      title: "Closing",
      key: "cq",
      width: w(110),
      align: "right" as const,
      render: (_: any, r: StockRegisterRow) => {
        const adjustment = Number(r.adjustmentQty) || 0;
        return (
          <div>
            {qty(r.closingQty)}
            {/* Opening + purchased − issued would not reach this balance on a row
                that was adjusted, so the adjustment is named here rather than
                carried in a column that is a dash on almost every row. */}
            {adjustment !== 0 && (
              <Tooltip title={`Includes an adjustment of ${fmtQty(adjustment)}${r.adjustmentReason ? ` — ${r.adjustmentReason}` : " with no reason recorded"}.`}>
                {note(
                  `incl. ${adjustment > 0 ? "+" : ""}${fmtQty(adjustment)} adj`,
                  MUTED
                )}
              </Tooltip>
            )}
            {Number(r.closingQty) < 0 && (
              <Tooltip title="More went out than came in — a receipt is probably not recorded yet.">
                {note("below zero")}
              </Tooltip>
            )}
          </div>
        );
      },
    },
    { title: "Closing ₹", key: "cv", width: w(120), align: "right" as const, render: (_: any, r: StockRegisterRow) => money(r.closingValue, { strong: true }) },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", width: w(170), ellipsis: true },
  ];

  if (loading && !register) return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
  if (error && !register) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={
          <Button size="small" onClick={load}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!register) return null;

  return (
    <div className="pms-exec-lines">
      <div className="pms-exec-lines-head">
        <span className="pms-exec-hint" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {filtered ? (
            <>
              {shownRows} movement{shownRows === 1 ? "" : "s"} match the search
              {oneDay ? "" : ` on ${days.length} of ${register.dayCount} days`}
            </>
          ) : oneDay ? (
            <>
              <span>
                {register.rowCount} movement{register.rowCount === 1 ? "" : "s"} on{" "}
                {dayjs(fromDate).format("DD-MMM-YYYY")}
              </span>
              <FlagChips
                flags={{
                  overNorm: register.overNormCount,
                  overTarget: register.overTargetCount,
                  mismatch: register.mismatchCount,
                  negative: register.negativeCount,
                }}
              />
            </>
          ) : (
            <>
              <span>
                {register.dayCount} day{register.dayCount === 1 ? "" : "s"} moved between{" "}
                {dayjs(fromDate).format("DD-MMM")} and {dayjs(toDate).format("DD-MMM-YYYY")} ·{" "}
                {register.rowCount} movement{register.rowCount === 1 ? "" : "s"}
              </span>
              <FlagChips
                flags={{
                  overNorm: register.overNormCount,
                  overTarget: register.overTargetCount,
                  mismatch: register.mismatchCount,
                  negative: register.negativeCount,
                }}
              />
            </>
          )}
        </span>
        <span className="pms-exec-cat-figures">
          <Button
            size="small"
            className="pms-exec-export-btn"
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            loading={exporting}
            disabled={!register.rowCount}
          >
            Export
          </Button>
          {flaggedCount > 0 && (
            <Segmented
              size="small"
              value={show}
              onChange={(v) => setShow(v as "all" | "flagged")}
              options={[
                { label: "All", value: "all" },
                { label: `Flagged (${flaggedCount})`, value: "flagged" },
              ]}
            />
          )}
          <span className="pms-exec-heads-figure">
            <span className="pms-exec-heads-subtotal">purchased ₹ {fmtQty(Number(register.purchasedValue) || 0)}</span>
          </span>
          <span className="pms-exec-heads-figure">
            <span className="pms-exec-heads-subtotal">issued ₹ {fmtQty(Number(register.issuedValue) || 0)}</span>
          </span>
          <span className="pms-exec-heads-figure">
            <Tooltip title="The closing of the last day that moved — what the store is carrying at the end of the range. Balances are not summed across days: the same stock would be counted once per day.">
              <span className="pms-exec-heads-subtotal">
                carrying ₹ {fmtQty(Number(register.closingValue) || 0)}
              </span>
            </Tooltip>
          </span>
        </span>
      </div>

      {!days.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            show === "flagged" && !search.trim() ? (
              <span>
                Nothing to explain
                {oneDay ? ` on ${dayjs(fromDate).format("DD-MMM-YYYY")}` : " in this range"}
                <br />
                <span style={{ color: MUTED, fontSize: 12 }}>
                  No receipt above the norm price, no issue above the day's allocation, nothing
                  disagreeing with the task issues and no negative balance.
                </span>
              </span>
            ) : filtered ? (
              <span>
                Nothing matching “{search.trim()}” moved
                {oneDay ? ` on ${dayjs(fromDate).format("DD-MMM-YYYY")}` : " in this range"}
                {show === "flagged" && " with anything to explain"}
              </span>
            ) : (
              <span>
                {oneDay ? (
                  <>Nothing moved in the store on {dayjs(fromDate).format("DD-MMM-YYYY")}</>
                ) : (
                  <>
                    Nothing moved in the store between {dayjs(fromDate).format("DD-MMM")} and{" "}
                    {dayjs(toDate).format("DD-MMM-YYYY")}
                  </>
                )}
                <br />
                <span style={{ color: MUTED, fontSize: 12 }}>
                  Balances carry forward on their own — only days with a receipt, an issue or an
                  adjustment are listed.
                </span>
              </span>
            )
          }
        />
      ) : (
        days.map((day) => (
          <div className="pms-exec-heads-block" key={day.date} style={{ marginBottom: 12 }}>
            <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
              <span>
                <span className="pms-eyebrow">{dayjs(day.date).format("DD-MMM-YYYY, ddd")}</span>
                <div className="pms-exec-hint" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span>
                    {day.materialCount} material{day.materialCount === 1 ? "" : "s"} in{" "}
                    {blocksOf(day).length} categor{blocksOf(day).length === 1 ? "y" : "ies"}
                  </span>
                  <FlagChips
                    flags={{
                      overNorm: day.overNormCount,
                      overTarget: day.overTargetCount,
                      mismatch: day.mismatchCount,
                      negative: day.negativeCount,
                    }}
                  />
                </div>
              </span>
              <span className="pms-exec-cat-figures">
                <span className="pms-exec-heads-figure">
                  <span className="pms-exec-heads-subtotal">opening ₹ {fmtQty(Number(day.openingValue) || 0)}</span>
                </span>
                <span className="pms-exec-heads-figure">
                  <span className="pms-exec-heads-subtotal">purchased ₹ {fmtQty(Number(day.purchasedValue) || 0)}</span>
                </span>
                <span className="pms-exec-heads-figure">
                  <span className="pms-exec-heads-subtotal">issued ₹ {fmtQty(Number(day.issuedValue) || 0)}</span>
                </span>
                <span className="pms-exec-heads-figure">
                  <span className="pms-exec-heads-subtotal">closing ₹ {fmtQty(Number(day.closingValue) || 0)}</span>
                </span>
              </span>
            </div>
            {blocksOf(day).map((block) => {
              const key = `${day.date}|${block.key}`;
              const open = toggled[key] ?? anyFlag(block.flags);
              return (
                <div key={key} style={{ marginBottom: 8 }}>
                  <div
                    className="pms-exec-lines-head"
                    style={{ marginBottom: open ? 4 : 0, cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => setToggled((t) => ({ ...t, [key]: !open }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setToggled((t) => ({ ...t, [key]: !open }));
                      }
                    }}
                  >
                    <span
                      className="pms-exec-hint"
                      style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
                    >
                      <b style={{ color: "#022c57" }}>
                        {open ? <CaretDownOutlined /> : <CaretRightOutlined />} {block.categoryName}
                      </b>
                      <span>
                        {block.rows.length} material{block.rows.length === 1 ? "" : "s"}
                      </span>
                      <FlagChips flags={block.flags} />
                    </span>
                    <span className="pms-exec-hint">
                      purchased ₹ {fmtQty(block.purchasedValue)} · issued ₹ {fmtQty(block.issuedValue)} · closing ₹{" "}
                      {fmtQty(block.closingValue)}
                    </span>
                  </div>
                  {open && (
                    <Table
                      size="small"
                      className="pms-exec-heads-table pms-stock-table"
                      rowKey={(row: StockRegisterRow) => `${day.date}-${row.materialId}`}
                      dataSource={block.rows}
                      columns={rowColumns as any}
                      pagination={false}
                      scroll={{ x: w(1500) }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      <div className="pms-exec-lines-foot">
        <span className="pms-exec-hint">
          Only days with a receipt, an issue or an adjustment are listed — a day without one carried
          its balance unchanged. Flags are checks, not errors: a receipt above the norm price, an issue
          above what that day's norms allocated, a difference from what the tasks booked, or a balance
          below zero. <Tag color="default">read-only</Tag> switch to Entry to record a day.
        </span>
      </div>
    </div>
  );
};

export default StockRegisterSection;
